import { CardKind, CommandResult, Commands } from "@highmarch/domain";
import type { Tower } from "@highmarch/domain";
import { groundAt } from "../render/terrain";
import type { UpgradeMark } from "../render/views/placement";
import type { UpgradeInHand } from "../ui/cardHand";
import { upgradeSelection } from "../ui/upgradePreview";
import type { LevelParts } from "./levelParts";

/** The Upgrade card in hand: the tower it is aimed at, its marks on the map, and what the tray says it does. */
export class UpgradeAim {
  /** A tower's height (world units) before its view has measured it: about an archer's. */
  static readonly FallbackTowerHeight = 1.4;
  /** The tower the Upgrade card in hand is aimed at (−1 until the player chooses one). */
  target = -1;

  constructor(private readonly p: LevelParts) {}

  /** The tower sheet's upgrade button: picks the offer's Upgrade card with that tower already chosen, to confirm. */
  useCardOn(towerId: number, cardIndex: number): void {
    this.target = towerId;
    if (this.p.execute(Commands.pickCard(cardIndex)) !== CommandResult.Ok) this.target = -1;
  }

  /** A new offer or pick: the aim holds only while an Upgrade card is in hand and its tower can still be raised. */
  settle(): void {
    const sim = this.p.sim, offer = sim.currentOffer;
    if (offer === null || !offer.hasPick || offer.pickedCard.kind !== CardKind.Upgrade) this.target = -1;
    const aimed = sim.findTower(this.target);
    if (aimed === null || !sim.canUpgrade(aimed)) this.target = -1;
  }

  /** The Upgrade card in hand, for the tray: how many towers it can raise and the chosen one's before → after. */
  inHand(): UpgradeInHand | null {
    const sim = this.p.sim, offer = sim.currentOffer;
    if (offer === null || !offer.hasPick || offer.pickedCard.kind !== CardKind.Upgrade) return null;
    const t = sim.findTower(this.target);
    return { targets: sim.towerSlots.filter((x) => sim.canUpgrade(x)).length, selected: t ? upgradeSelection(sim, t) : null };
  }

  /** The Upgrade card's marks: its targets in gold, max-tier towers dimmed, the chosen one with its next range. */
  showMarks(): void {
    const sim = this.p.sim, alive = sim.towerSlots.filter((t) => t.alive), towers = this.p.scene.towers;
    const mark = (t: Tower): UpgradeMark => ({ cell: t.cell, top: towers.restBox(t.id)?.max.y ?? groundAt(t.cell.x, t.cell.y) + UpgradeAim.FallbackTowerHeight });
    const chosen = sim.findTower(this.target);
    const ranges = chosen ? { now: sim.rangeOf(chosen).toNumber(), next: sim.previewRange(chosen.archetype, chosen.tier + 1).toNumber() } : null;
    this.p.scene.placement.showUpgradeTargets(alive.filter((t) => sim.canUpgrade(t)).map(mark), alive.filter((t) => !sim.canUpgrade(t)).map((t) => t.cell), chosen ? mark(chosen) : null, ranges);
  }
}
